import { NgStyle, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UniqueComponentId } from 'primeng/utils';
import { BusinessDetails } from '@App/common/models';
import { PitchCardModalsWrapperService } from '@App/modules/pitch-card-shared/services';
import { CommonBindingDataService } from '@Shared/services';
import { CardPackageService } from '@App/modules/cards-packages/services';
import { ScreenSizeService } from '@Shared/services';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PitchCardModalsWrapperComponent } from '../../modules/pitch-card-shared/components/pitchcard-modals-wrapper/pitchcards-modals-wrapper.component';
import { PitchCardComponent as PitchCardComponent_1 } from '../../modules/pitch-card-shared/components/pitch-card/pitch-card.component';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { debounceTime, tap, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PitchCardSocialMetaService } from './pitch-card-social-meta.service';

@Component({
  selector: 'app-pitch-card-share',
  templateUrl: './pitch-card.component.html',
  styleUrls: ['./pitch-card.component.scss'],
  standalone: true,
  imports: [
    PitchCardComponent_1,
    PitchCardModalsWrapperComponent,
    ConfirmDialogModule,
    NgStyle
  ],
  encapsulation: ViewEncapsulation.None
})
export class PitchCardComponent implements OnInit, AfterViewInit, OnDestroy {
  alias: string;
  businessDetails: BusinessDetails | null;
  uniqueId: string;
  private startTrackTime = 0;
  private inactiveUserStart = 0;
  private userInactivityTime = 10000; // 10 secs
  private beforeUnloadHandler: (() => void) | null = null;

  isBrowser = false;

  isPlayThumbnailVideo = true;
  windowHeight = '100vh';
  isMobile = false;
  isSafari = false;
  safariFactor = 1;
  heightPadding = 15;

  @ViewChild('previewCard') previewCard: PitchCardComponent_1;

  @ViewChild('pitchCardWrapper', { static: false })
  pitchCardWrapper: ElementRef;

  constructor(
    private activateRoute: ActivatedRoute,
    private router: Router,
    private screenSizeService: ScreenSizeService,
    private cardPackageService: CardPackageService,
    private pitchCardModalsWrapperService: PitchCardModalsWrapperService,
    private commonBindingService: CommonBindingDataService,
    private socialMeta: PitchCardSocialMetaService,
    private changeDetectorRef: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  @HostBinding('style.--height-padding') get paddings() {
    return this.heightPadding;
  }

  @HostBinding('style.--scale-factor') get scale() {
    return this.cardPackageService.getScaleFactor(
      this.isSafari && this.isMobile,
      this.safariFactor
    );
  }

  @HostListener('window:click', ['$event'])
  handleClick(event: any) {
    const classes = event.target?.classList;
    const parentNodes = this.commonBindingService.getParentNodes(
      classes,
      'contact-menu-container'
    );

    if (
      !parentNodes?.length &&
      !event.target.classList.contains('contact-menu-button') &&
      this.previewCard
    ) {
      this.previewCard.toggleContactMenu(null);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (isPlatformBrowser(this.platformId)) {
      this.windowHeight = window.innerHeight + 'px';
      this.isMobile = this.screenSizeService.isMobile;
    }
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(event) {
    this.safariFactor = null;
  }

  ngOnInit(): void {
    this.activateRoute.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ card }) => {
        this.alias = this.activateRoute.snapshot.paramMap.get('alias') || '';
        this.businessDetails = card ?? null;

        if (this.businessDetails) {
          this.socialMeta.apply(this.businessDetails, this.alias);
        } else {
          this.socialMeta.applyNotFound();
        }
      });

    // Track closing tab and refresh page
    this.startTrackTime = Date.now();
    this.trackUserActivity();
    if (isPlatformBrowser(this.platformId)) {
      this.beforeUnloadHandler = () => {
        const trackedTime = Date.now() - this.startTrackTime;
        const seconds = Math.ceil(trackedTime / 1000);
        const businessId = this.businessDetails?.id;

        if (!businessId) {
          return;
        }

        navigator.sendBeacon(
          `customer-analytics/${seconds}/${businessId}/add-view-pitchcard`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    this.isBrowser = isPlatformBrowser(this.platformId);
    this.uniqueId = UniqueComponentId();

    this.isPlayThumbnailVideo = this.activateRoute?.snapshot?.queryParams
      ?.thumbnail
      ? !JSON.parse(this.activateRoute.snapshot.queryParams.thumbnail)
      : true;

    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'relative';
      document.body.style.height = '100%';
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    requestAnimationFrame(() => {
      this.isMobile = this.screenSizeService.isMobile;
      this.isSafari = this.screenSizeService.browser === 'Safari';
      this.windowHeight = window.innerHeight + 'px';
      this.safariFactor = this.cardPackageService.getScaleFactor(
        this.isSafari && this.isMobile
      );
      this.heightPadding = this.cardPackageService.getPaddings();
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      const trackedTime = Date.now() - this.startTrackTime;
      const seconds = Math.ceil(trackedTime / 1000);
      const businessId = this.businessDetails?.id;

      if (businessId) {
        navigator.sendBeacon(
          `customer-analytics/${seconds}/${businessId}/add-view-pitchcard`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }

      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      }
      this.socialMeta.clear();
    }
  }

  trackUserActivity() {
    if (isPlatformBrowser(this.platformId)) {
      merge(
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'pointermove')
      )
        .pipe(
          tap(() => {
            if (!this.inactiveUserStart) return;
            const inactiveTime = Date.now() - this.inactiveUserStart; // milliseconds of inactivity
            this.startTrackTime = this.startTrackTime + inactiveTime; // shifting start timestamp on pause time
            this.inactiveUserStart = 0;
          }),
          takeUntilDestroyed(this.destroyRef),
          debounceTime(this.userInactivityTime)
        )
        .subscribe(() => (this.inactiveUserStart = Date.now()));
    }
  }

  goToPage() {
    this.router.navigate(['/pricing']);
  }
  playInlineVideo() {
    if (this.businessDetails?.videoFileUrl) {
      setTimeout(() => {
        this.previewCard.initVideo = true;
        // this.previewCard.loadVideo({ muted: true });
      }, 3000);
    }
  }

  handlePitchCardTitleClick(clickEvent: any) {
    const { title } = clickEvent;
    this.pitchCardModalsWrapperService.setUniqueId(this.uniqueId);
    this.pitchCardModalsWrapperService.setCurrentBusiness(
      this.businessDetails as BusinessDetails
    );
    this.pitchCardModalsWrapperService.onTitleClick(title);
  }

  getScaleFactor() {
    return this.cardPackageService.getScaleFactor();
  }

  stopScroll(e) {
    if (this.isMobile && this.isSafari) {
      e.preventDefault();
    }
  }
}
